/**
 * RemoteSharks — everybody else's animal.
 *
 * A peer arrives as two numbers, a species index and a seed, and that is genuinely all: the
 * genome is derived from them and the mesh built locally in the same worker that builds the
 * player's. No geometry crosses the network, ever.
 *
 * MOTION IS INTERPOLATED, NOT SIMULATED
 * The obvious thing — replay the peer's controls through the same physics — is wrong here. It
 * needs the two ends to agree on every step for ever, and the moment they drift there is no
 * correction that is not a visible snap. Instead each peer keeps a short buffer of received
 * poses and is drawn slightly in the PAST, between two snapshots that have both already
 * arrived. Interpolating between known facts cannot diverge, cannot snap, and costs one
 * delay-length of lag that nobody in a reef can perceive.
 *
 * What IS simulated locally is everything the network does not carry: the swim wave, the bank
 * into a turn, the body bend. Those are functions of speed and heading rate, so deriving them
 * here at full frame rate looks better than receiving them at twelve — and it means a peer on
 * a bad connection still swims smoothly, it just swims smoothly along a slightly stale path.
 */
import * as THREE from 'three';
import { MOVEMENT, SWIM } from '../data/tuning.ts';
import { SharkMaterial, swimParamsForSpeed } from '../render/SharkMaterial.ts';
import { speciesKey } from '../voxel/SharkForge.ts';
import type { MeshService } from '../voxel/MeshService.ts';
import type { PeerPose } from '../../shared/protocol.js';
import type { PeerInfo } from '../net/NetClient.ts';
import type { SkyEnv } from '../world/DayNight.ts';
import { applyLure, type LureState } from '../render/LureLight.ts';

/**
 * How far behind live to draw peers, in SENDER time.
 *
 * Two and a bit send intervals. It was 1.7, chosen against a perfectly regular stream, and the
 * real internet is not one: anything later than the buffer leaves nothing to interpolate
 * toward, and the peer stalls and then jumps. The cost of more is pure added lag, which on an
 * animal that turns this slowly nobody can see.
 */
const DELAY = 0.19;
/** older than this and a snapshot can never be needed again */
const KEEP = 1.2;
/**
 * How far past the newest sample dead reckoning will carry a peer before it gives up and
 * holds still.
 *
 * Short on purpose. Extrapolation is a guess, and the error grows with the square of how long
 * you guess for; a shark carried a quarter second on stale heading is at most a body-length
 * out and slides back invisibly, while one carried two seconds swims through the reef.
 */
const EXTRAPOLATE = 0.25;

interface Sample extends PeerPose {
  t: number;
}

interface Peer {
  info: PeerInfo;
  root: THREE.Group;
  mesh: THREE.Mesh | null;
  material: SharkMaterial;
  buffer: Sample[];
  /** drawn state, eased toward the interpolated target */
  heading: number;
  bank: number;
  curve: number;
  speed: number;
  ready: boolean;
}

export class RemoteSharks {
  readonly group = new THREE.Group();

  private readonly peers = new Map<number, Peer>();
  private readonly mesh: MeshService;
  /**
   * local clock minus relay clock, seconds. Maps sender timestamps onto our own timeline.
   *
   * Estimated as the MINIMUM seen rather than an average, because that is the least-delayed
   * packet and therefore the closest to the true offset — every other sample is that plus
   * however long the network held it. Adopted instantly when a faster path appears and given
   * up only slowly, so one lucky packet cannot drag the whole timeline forward and one slow
   * stretch cannot permanently bias it.
   */
  private offset = Number.NaN;
  /** bumped whenever a peer's mesh appears or a peer leaves, so casters get recollected */
  dirty = false;

  constructor(mesh: MeshService) {
    this.mesh = mesh;
    this.group.name = 'remote-sharks';
  }

  get count(): number {
    return this.peers.size;
  }

  /** where a peer is right now, or null if we do not have them — for anything that rides along */
  poseOf(id: number): { x: number; y: number; z: number; heading: number } | null {
    const p = this.peers.get(id);
    if (!p || !p.ready || !p.root.visible) return null;
    return { x: p.root.position.x, y: p.root.position.y, z: p.root.position.z, heading: p.heading };
  }

  get readyCount(): number {
    let n = 0;
    for (const p of this.peers.values()) if (p.ready) n++;
    return n;
  }

  add(info: PeerInfo): void {
    if (this.peers.has(info.id)) return;
    const material = new SharkMaterial({ jaw: true });
    const root = new THREE.Group();
    root.rotation.order = 'YXZ';
    root.visible = false;
    this.group.add(root);

    const p: Peer = {
      info, root, mesh: null, material, buffer: [],
      heading: 0, bank: 0, curve: 0, speed: 0, ready: false,
    };
    this.peers.set(info.id, p);

    /* Built from the spec alone — the same call the local player makes, on the same worker.
       A peer who joined before us and one who joins after get identical animals, because the
       forge is a pure function of these five numbers and nothing about the mesh crosses the
       network. Verified: the same spec produces byte-identical geometry. */
    void this.mesh
      .buildSpec({
        species: speciesKey(info.species),
        seed: info.seed >>> 0,
        size: info.size,
        age: info.age,
        name: info.name,
      })
      .then((built) => {
        // They may have left while their mesh was in the worker.
        if (!this.peers.has(info.id)) {
          built.geometry.dispose();
          return;
        }
        material.setSpineLength(built.size.z);
        material.setJawRig(built.jaw);
        const m = new THREE.Mesh(built.geometry, material);
        m.frustumCulled = false;
        root.add(m);
        p.mesh = m;
        p.ready = true;
        this.dirty = true;
      })
      .catch((err: unknown) => {
        console.error('[RemoteSharks] peer mesh failed', info, err);
      });
  }

  remove(id: number): void {
    const p = this.peers.get(id);
    if (!p) return;
    this.peers.delete(id);
    p.mesh?.geometry.dispose();
    p.material.dispose();
    this.group.remove(p.root);
    this.dirty = true;
  }

  clear(): void {
    for (const id of [...this.peers.keys()]) this.remove(id);
  }

  /**
   * A snapshot arrived. It is filed under the time the RELAY sent it, not the time it got
   * here — that difference is the whole reason peers used to judder. Arrival times carry the
   * network's jitter, so poses sent 83 ms apart and arriving 60 and 110 ms apart described a
   * peer covering the same ground in different amounts of time, and it visibly surged and
   * stalled. The sender's clock is uniform by construction.
   *
   * @param now    local seconds, for estimating the offset between the two clocks
   * @param sentAt the relay's clock in milliseconds
   */
  receive(now: number, sentAt: number, poses: PeerPose[]): void {
    const t = sentAt / 1000;
    const est = now - t;
    if (!Number.isFinite(this.offset) || Math.abs(est - this.offset) > 3) {
      // First packet, or the relay restarted and its clock went back to zero.
      this.offset = est;
      for (const p of this.peers.values()) p.buffer.length = 0;
    } else if (est < this.offset) {
      this.offset = est;
    } else {
      this.offset += (est - this.offset) * 0.02;
    }

    for (const q of poses) {
      const p = this.peers.get(q.id);
      if (!p) continue;
      p.buffer.push({ ...q, t });
      // A peer whose packets stop leaves a buffer that would otherwise grow forever.
      while (p.buffer.length > 2 && t - p.buffer[0].t > KEEP) p.buffer.shift();
    }
  }

  update(now: number, dt: number, time: number): void {
    if (!Number.isFinite(this.offset)) return;
    // Render time on the RELAY's clock, which is the axis every sample is filed under.
    const at = now - this.offset - DELAY;

    for (const p of this.peers.values()) {
      if (!p.ready || p.buffer.length === 0) continue;

      const last = p.buffer[p.buffer.length - 1];
      let x: number;
      let y: number;
      let z: number;
      let targetHeading: number;
      let targetSpeed: number;

      if (at > last.t) {
        /* Past everything we have been told. Rather than freeze on the last pose and jump when
           the next packet lands — which is the stall-and-snap this whole file exists to avoid —
           carry the peer forward on its last known heading and speed. A shark holds a course;
           over a quarter second that guess is very nearly right, and the correction when the
           real pose arrives is smaller than the jump would have been. */
        const ahead = Math.min(at - last.t, EXTRAPOLATE);
        // Nose is at high Z in model space, so heading h points along (sin h, cos h).
        x = last.x + Math.sin(last.heading) * last.speed * ahead;
        y = last.y;
        z = last.z + Math.cos(last.heading) * last.speed * ahead;
        targetHeading = last.heading;
        targetSpeed = last.speed;
      } else {
        // Find the two samples that straddle the render time.
        let a = p.buffer[0];
        let b = last;
        for (let i = 0; i < p.buffer.length - 1; i++) {
          if (p.buffer[i].t <= at && p.buffer[i + 1].t >= at) {
            a = p.buffer[i];
            b = p.buffer[i + 1];
            break;
          }
        }
        const span = b.t - a.t;
        const f = span > 1e-4 ? Math.min(1, Math.max(0, (at - a.t) / span)) : 0;

        x = a.x + (b.x - a.x) * f;
        y = a.y + (b.y - a.y) * f;
        z = a.z + (b.z - a.z) * f;
        // Headings must be blended the short way round or a peer crossing the wrap spins a
        // full circle in one frame.
        let dh = b.heading - a.heading;
        while (dh > Math.PI) dh -= Math.PI * 2;
        while (dh < -Math.PI) dh += Math.PI * 2;
        targetHeading = a.heading + dh * f;
        targetSpeed = a.speed + (b.speed - a.speed) * f;
      }

      /* Derive the body language from the heading RATE, exactly as the local player does:
         roll scales with speed because a shark needs lateral load to lean against, and body
         bend scales the other way because a tight turn is achieved by folding. Sending these
         would cost bandwidth to look worse. */
      let turn = targetHeading - p.heading;
      while (turn > Math.PI) turn -= Math.PI * 2;
      while (turn < -Math.PI) turn += Math.PI * 2;
      const rate = dt > 1e-5 ? turn / dt : 0;
      const t = Math.min(1, targetSpeed / MOVEMENT.cruiseSpeed);
      const wantBank = Math.max(-1, Math.min(1, rate * 1.6)) * MOVEMENT.bankMax * (0.35 + 0.65 * t);
      const wantCurve = Math.max(-1, Math.min(1, rate * 1.6)) * SWIM.curveMax
        * (1 - (1 - SWIM.curveAtCruise) * t);

      /* Position is EASED toward the interpolated target rather than written to it. Even on a
         clean timeline a correction lands whenever extrapolation guessed slightly wrong, and
         writing it straight in is a visible tick. A short spring makes it a slide nobody
         notices. Snap outright past a body length, which means a genuine teleport or a peer
         that has been gone long enough that pretending otherwise would look worse. */
      const k = 1 - Math.exp(-14 * dt);
      const jump = Math.abs(x - p.root.position.x) + Math.abs(z - p.root.position.z);
      if (!p.root.visible || jump > 9) {
        p.root.position.set(x, y, z);
      } else {
        p.root.position.x += (x - p.root.position.x) * k;
        p.root.position.y += (y - p.root.position.y) * k;
        p.root.position.z += (z - p.root.position.z) * k;
      }

      p.heading = targetHeading;
      p.speed = targetSpeed;
      p.bank += (wantBank - p.bank) * (1 - Math.exp(-MOVEMENT.bankResponse * dt));
      p.curve += (wantCurve - p.curve) * (1 - Math.exp(-SWIM.curveResponse * dt));

      const swim = swimParamsForSpeed(p.speed, MOVEMENT.cruiseSpeed);
      p.material.setSwim(swim.amp, swim.speed);
      p.material.setCurve(p.curve);
      p.material.setTime(time);

      p.root.rotation.set(0, p.heading, p.bank, 'YXZ');
      p.root.visible = true;
    }
  }

  setEnv(env: SkyEnv): void {
    for (const p of this.peers.values()) p.material.setEnv(env);
  }

  /** peers are lit by the anglerfish's lure like everything else wearing a SharkMaterial */
  applyLure(lure: LureState): void {
    for (const p of this.peers.values()) applyLure(p.material.uniforms, lure);
  }

  /** peers cast and receive like everything else wearing a SharkMaterial */
  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    for (const p of this.peers.values()) p.material.setShadow(map, matrix, strength);
  }

  dispose(): void {
    this.clear();
    this.group.removeFromParent();
  }
}
